import { useState } from 'react';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { attachmentsApi } from '../features/attachments/attachmentsApi';
import type { AttachmentResource } from '../features/attachments/types';
import { useAttachments } from '../features/attachments/useAttachments';
import { FileUploadButton } from './FileUploadButton';
import { DateCell } from './DateCell';
import { showToast } from './ui/toast.utils';

type Props = {
  resourceType: AttachmentResource;
  resourceId: string;
  readOnly?: boolean;
  accept?: string;
  title?: string;
};

export function AttachmentsList({
  resourceType,
  resourceId,
  readOnly = false,
  accept,
  title = 'Adjuntos',
}: Props) {
  const { confirm } = useConfirmDialog();
  const { items, loading, error, reload } = useAttachments(resourceType, resourceId);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDownload = async (id: string, fileName: string) => {
    try {
      await attachmentsApi.download(id, fileName);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'No se pudo descargar el archivo';
      showToast(msg, 'error');
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Eliminar adjunto',
      message: '¿Eliminar este adjunto?',
      confirmText: 'Eliminar',
      variant: 'danger',
    });
    if (!ok) return;
    setDeletingId(id);
    try {
      await attachmentsApi.remove(id);
      await reload();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {!readOnly && resourceId && (
          <FileUploadButton
            resourceType={resourceType}
            resourceId={resourceId}
            accept={accept}
            onUploaded={() => reload()}
          />
        )}
      </div>

      {loading && <p className="text-sm text-muted-foreground">Cargando adjuntos…</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {!loading && items.length === 0 && (
        <p className="text-sm text-muted-foreground">Sin adjuntos</p>
      )}

      {items.length > 0 && (
        <div className="border rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Nombre</th>
                <th className="px-3 py-2 text-left font-medium">Tipo</th>
                <th className="px-3 py-2 text-left font-medium">Tamaño</th>
                <th className="px-3 py-2 text-left font-medium">Subido por</th>
                <th className="px-3 py-2 text-left font-medium">Fecha</th>
                <th className="px-3 py-2 text-left font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t">
                  <td className="px-3 py-2 break-all">{a.fileName}</td>
                  <td className="px-3 py-2 text-muted-foreground">{simplifyMime(a.mimeType)}</td>
                  <td className="px-3 py-2">{formatBytes(a.sizeBytes)}</td>
                  <td className="px-3 py-2 text-xs">{a.uploadedBy?.email ?? '—'}</td>
                  <td className="px-3 py-2">
                    <DateCell value={a.createdAt} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-primary hover:underline text-xs"
                        onClick={() => void handleDownload(a.id, a.fileName)}
                      >
                        Descargar
                      </button>
                      {!readOnly && (
                        <button
                          type="button"
                          className="text-destructive hover:underline text-xs disabled:opacity-50"
                          disabled={deletingId === a.id}
                          onClick={() => void handleDelete(a.id)}
                        >
                          Borrar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function simplifyMime(mime: string): string {
  const bounded = mime.slice(0, 120);
  if (bounded.startsWith('image/')) return 'Imagen';
  if (bounded === 'application/pdf') return 'PDF';
  if (bounded.includes('word')) return 'Word';
  if (bounded.includes('sheet') || bounded.includes('excel')) return 'Excel';
  if (bounded.startsWith('video/')) return 'Video';
  if (bounded === 'application/zip') return 'ZIP';
  return bounded;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}
